import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Divider, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Avatar, 
  CircularProgress, 
  Alert, 
  AlertTitle,
  Chip,
  IconButton
} from '@mui/material';
import { format } from 'date-fns';
import { getBlogPostBySlug, addComment, deleteBlogPost } from '../services/blogService';
import { useAuth } from '../context/AuthContext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import AttachmentsList from '../components/AttachmentsList';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const data = await getBlogPostBySlug(slug);
        setPost(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post. It may have been deleted or doesn\'t exist.');
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) {
      setCommentError('Comment cannot be empty');
      return;
    }

    try {
      const newComment = await addComment(post._id, commentText);
      
      // Update the post with the new comment
      setPost({
        ...post,
        comments: [...post.comments, newComment]
      });
      
      setCommentText('');
      setCommentError(null);
    } catch (err) {
      console.error('Error adding comment:', err);
      setCommentError('Failed to add comment. Please try again.');
    }
  };

  const handleDeletePost = async () => {
    try {
      await deleteBlogPost(post._id);
      navigate('/blog');
    } catch (err) {
      console.error('Error deleting blog post:', err);
      setError('Failed to delete blog post. Please try again later.');
    }
    setDeleteDialogOpen(false);
  };

  const isAuthor = post && user && post.author._id === user.id;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
        <Button component={RouterLink} to="/blog" variant="contained">
          Back to Blog
        </Button>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="info">
          Post not found. It may have been deleted or doesn't exist.
        </Alert>
        <Button component={RouterLink} to="/blog" variant="contained" sx={{ mt: 2 }}>
          Back to Blog
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Alert severity="info" sx={{ mb: 4 }}>
        <AlertTitle>Important Notice</AlertTitle>
        This information is for educational purposes only and should not replace professional medical advice. 
        Always consult with healthcare providers for personalized diagnosis and treatment.
      </Alert>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Button component={RouterLink} to="/blog" sx={{ mb: 2 }}>
            ← Back to Blog
          </Button>
          <Typography variant="h3" component="h1" gutterBottom>
            {post.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar sx={{ width: 24, height: 24 }}>
              {post.anonymous ? 'A' : (post.author.name ? post.author.name.charAt(0).toUpperCase() : 'U')}
            </Avatar>
            <Typography variant="body2" color="text.secondary">
              {post.anonymous ? 'Anonymous' : (post.author.name || 'Unknown')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {format(new Date(post.createdAt), 'MMM d, yyyy')}
            </Typography>
          </Box>
          {post.tags && post.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {post.tags.map(tag => (
                <Chip 
                  key={tag} 
                  label={tag} 
                  size="small" 
                  component={RouterLink}
                  to={`/blog?tag=${tag}`}
                  clickable
                  sx={{ textDecoration: 'none' }}
                />
              ))}
            </Box>
          )}
        </Box>
        
        {isAuthor && (
          <Box>
            <IconButton 
              component={RouterLink} 
              to={`/blog/edit/${post._id}`}
              aria-label="edit"
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              onClick={() => setDeleteDialogOpen(true)}
              aria-label="delete"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {post.featuredImage && (
        <Box 
          component="img" 
          src={post.featuredImage} 
          alt={post.title}
          sx={{ 
            width: '100%', 
            maxHeight: 400, 
            objectFit: 'cover', 
            borderRadius: 1,
            mb: 4
          }}
        />
      )}

      <Box 
        sx={{ 
          mb: 6,
          '& img': { 
            maxWidth: '100%',
            height: 'auto' 
          },
          '& a': {
            color: 'primary.main'
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            mt: 3,
            mb: 2
          },
          '& p': {
            mb: 2
          }
        }}
        dangerouslySetInnerHTML={{ __html: post.content }}
      />
      
      {/* Display attachments if any */}
      {post.attachments && post.attachments.length > 0 && (
        <AttachmentsList attachments={post.attachments} />
      )}

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h5" component="h2" gutterBottom>
          Comments ({post.comments.length})
        </Typography>

        {isAuthenticated ? (
          <Box component="form" onSubmit={handleSubmitComment} sx={{ mb: 4 }}>
            <TextField
              fullWidth
              label="Add a comment"
              multiline
              rows={3}
              margin="normal"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              error={!!commentError}
              helperText={commentError}
            />
            <Button type="submit" variant="contained" sx={{ mt: 1 }}>
              Post Comment
            </Button>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography>
              Please <RouterLink to="/signin">sign in</RouterLink> to add a comment.
            </Typography>
          </Alert>
        )}

        {post.comments.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ my: 4, textAlign: 'center' }}>
            No comments yet. Be the first to share your thoughts!
          </Typography>
        ) : (
          <Box sx={{ mt: 2 }}>
            {post.comments.map((comment, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                      {comment.user?.name ? comment.user.name.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {comment.user?.name || 'Anonymous'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(comment.createdAt), 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {comment.text}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Blog Post</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this blog post? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePost} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BlogPost; 